import { useState } from 'react';
import { App as AntdApp, Layout, Button, Select, Dropdown, Avatar, Space } from 'antd';
import {
  MenuOutlined,
  GlobalOutlined,
  UserOutlined,
  CloseOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ALL_ROLES, useRole, type RoleId } from '@/auth/roles';
import GlobalSearch from './GlobalSearch';
import NotificationBell from './NotificationBell';
import AboutModal from './AboutModal';
import BrandLockup from './BrandLockup';

const { Header } = Layout;

interface AppHeaderProps {
  onToggleSider: () => void;
  onOpenRecents: () => void;
}

export default function AppHeader({ onToggleSider, onOpenRecents }: AppHeaderProps) {
  const { t, i18n } = useTranslation();
  const { role, setRole } = useRole();
  const { message } = AntdApp.useApp();
  const [bannerOpen, setBannerOpen] = useState(true);
  const [aboutOpen, setAboutOpen] = useState(false);

  const isArabic = i18n.language?.startsWith('ar');

  const toggleLang = () => {
    const next = isArabic ? 'en' : 'ar';
    void i18n.changeLanguage(next);
    void message.info(
      t(next === 'ar' ? 'common:toast.languageArabic' : 'common:toast.languageEnglish'),
    );
  };

  const handleRoleChange = (next: RoleId) => {
    setRole(next);
    void message.info(t('common:toast.roleSwitched', { role: t(`common:roles.${next}`) }));
  };

  const roleOptions = ALL_ROLES.map((r) => ({
    value: r,
    label: t(`roles.${r}`),
  }));

  const userMenu = {
    items: [
      { key: 'profile', label: t('header.profile') },
      {
        key: 'about',
        label: t('header.aboutPrototype'),
        icon: <InfoCircleOutlined />,
      },
      { type: 'divider' as const },
      { key: 'signOut', label: t('header.signOut') },
    ],
    onClick: (info: { key: string }) => {
      if (info.key === 'about') setAboutOpen(true);
    },
  };

  return (
    <>
      <Header
        style={{
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: '1px solid rgba(184, 137, 62, 0.18)',
        }}
      >
        <Button
          type="text"
          icon={<MenuOutlined style={{ color: '#fff' }} />}
          onClick={onToggleSider}
          aria-label={t('header.toggleSidebar')}
        />
        <BrandLockup />

        {bannerOpen && (
          <span
            className="ga-prototype-ribbon"
            onClick={() => setBannerOpen(false)}
            role="button"
            tabIndex={0}
            aria-label={t('app.prototypeBanner')}
            style={{ marginInlineStart: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setBannerOpen(false);
            }}
          >
            {t('app.prototypeBanner')}
            <CloseOutlined style={{ fontSize: 9, opacity: 0.85 }} />
          </span>
        )}

        <div className="ga-header-spacer" />

        <div className="ga-header-controls">
          <GlobalSearch />
          <Button
            icon={<GlobalOutlined />}
            onClick={toggleLang}
            aria-label={t('header.language')}
          >
            {isArabic ? 'EN' : 'AR'}
          </Button>
          <Select<RoleId>
            value={role}
            onChange={handleRoleChange}
            options={roleOptions}
            style={{ width: 180 }}
            aria-label={t('header.role')}
          />
          <Button
            type="text"
            icon={<ClockCircleOutlined style={{ color: '#fff' }} />}
            onClick={onOpenRecents}
            aria-label={t('header.recents')}
            title={t('header.recents')}
          />
          <NotificationBell />
          <Dropdown menu={userMenu} trigger={['click']}>
            <Space style={{ color: '#fff', cursor: 'pointer' }}>
              <Avatar icon={<UserOutlined />} />
            </Space>
          </Dropdown>
        </div>
      </Header>
      <AboutModal open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </>
  );
}
